import * as React from "react";
import { createRoot, Root } from "react-dom/client";
import TourBuilderRoot from "../tourBuilder/TourBuilderRoot";
import { TOUR_BUILDER_STYLES } from "../tourBuilder/tourBuilder.styles";

// Survives in-tab navigation the same way TourRunner's progress does: the
// admin can click around their real site while in builder mode, and this
// re-mounts the builder on every page load without needing the token back
// in the URL (which we strip immediately - see start()).
const STORAGE_KEY = "bizzey_tour_builder_session";
const HOST_ID = "bizzey-tour-builder-host";

interface BuilderSession {
  token: string;
}

class TourBuilder {
  private static root: Root | null = null;
  private static hostElement: HTMLElement | null = null;

  static start(token: string) {
    this.persist({ token });
    this.stripTokenFromUrl();
    this.mount(token);
  }

  static resumeIfPending() {
    const session = this.read();
    if (!session) return;

    this.mount(session.token);
  }

  static exit() {
    this.clear();
    this.unmount();
  }

  private static stripTokenFromUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete("bizzey_tour_builder");
    window.history.replaceState({}, "", url.toString());
  }

  private static mount(token: string) {
    if (this.root) return;

    const host = document.createElement("div");
    host.id = HOST_ID;
    document.body.appendChild(host);

    const shadowRoot = host.attachShadow({ mode: "open" });

    const style = document.createElement("style");
    style.textContent = TOUR_BUILDER_STYLES;
    shadowRoot.appendChild(style);

    const mountPoint = document.createElement("div");
    shadowRoot.appendChild(mountPoint);

    this.hostElement = host;
    this.root = createRoot(mountPoint);
    this.root.render(
      React.createElement(TourBuilderRoot, {
        token,
        onExit: () => this.exit(),
      }),
    );
  }

  private static unmount() {
    this.root?.unmount();
    this.root = null;
    this.hostElement?.remove();
    this.hostElement = null;
  }

  private static persist(session: BuilderSession) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  }

  private static read(): BuilderSession | null {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  private static clear() {
    sessionStorage.removeItem(STORAGE_KEY);
  }
}

export default TourBuilder;
